import React, { createContext, useContext, useState, useEffect } from "react";
import { API_BASE_URL } from "../config/api"; 

interface UserDetails {
    email: string;
    memberUuid: string;
    name: string;
    userName: string;
    id: string;
    memberId: number;
}

interface AuthContextType {
    user: UserDetails | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const memberUuid = localStorage.getItem('memberUuid');
        const userEmail = localStorage.getItem('userEmail');
        const userName = localStorage.getItem('userName');
        
        if (memberUuid && userEmail) {
            setUser({
                email: userEmail,
                memberUuid: memberUuid,
                name: userName || userEmail,
                userName: userEmail,
                id: memberUuid,
                memberId: 0
            });
        } else {
            setUser(null);
        }
        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok && data.message === "Successfully logged in") {
                localStorage.setItem('memberUuid', data.memberUuid);
                localStorage.setItem('userEmail', email);
                localStorage.setItem('userName', email); // Using email as name if not provided

                setUser({
                    email: email,
                    memberUuid: data.memberUuid,
                    name: email,
                    userName: email,
                    id: data.memberUuid,
                    memberId: 0
                });
                return true;
            } else {
                return false;
            }
        } catch (err) {
            console.error("Login error:", err);
            return false;
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('memberUuid');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
    };

    if (isLoading) {
        return <div className="text-white text-center mt-10">Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within AuthProvider");
    return context;
};